import { delay } from "redux-saga";
import qs from "qs";
import cookies from 'js-cookie';
import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
  fork,
  take
} from "redux-saga/effects";
import { LOCATION_CHANGE } from "react-router-redux";
import Raven from "raven-js";

import {
  USER_FETCH_REQUESTED,
  USER_FETCH_SUCCEEDED,
  USER_FETCH_FAILED,
  CHANGE_UNIVERSITY,
  CHANGE_QUERY,
  CHANGE_QUERY_REQUESTED,
  SEARCH_REQUESTED,
  SEARCH_SUCCEEDED,
  SEARCH_FAILED,
  PROFESSOR_REQUESTED,
  PROFESSOR_SUCCEEDED,
  PROFESSOR_FAILED,
  NEW_COMMENT_REQUESTED,
  NEW_COMMENT_SUCCEEDED,
  NEW_COMMENT_FAILED,
  INAPPROPRIATE_REQUESTED,
  INAPPROPRIATE_SUCCEEDED,
  INAPPROPRIATE_FAILED,
  COMMENT_VOTE_REQUESTED,
  COMMENT_VOTE_SUCCEEDED,
  COMMENT_VOTE_FAILED,
  PROF_VOTE_REQUESTED,
  PROF_VOTE_SUCCEEDED,
  PROF_VOTE_FAILED
} from "./actions.js";
import { suggestionsSaga } from "./containers/SuggestionsContainer.js";
import { BAD_COMMENT_SUBMITTED } from "./containers/profecomments/NewCommentContainer.js";
import { jsonOrThrowError, SEARCH_PAGE_REGEX, hasBadWord } from "./util.js";

function apiFetch(method, jsonBody) {
  return function(url) {
    const csrftoken = cookies.get('csrftoken');
    const options = { method: method, headers: {'X-CSRFToken': csrftoken} };
    if (jsonBody !== undefined) {
      options.body = JSON.stringify(jsonBody);
    }
    return fetch(url, options).then(jsonOrThrowError);
  };
}

function* fetchUser(action) {
  try {
    const json = yield call(apiFetch("GET"), "/api/users/current/data");
    yield put({ type: USER_FETCH_SUCCEEDED, data: json });
  } catch (e) {
    yield put({ type: USER_FETCH_FAILED });
  }
}

// === For incremental search
function* runRequestSuggest(u, q) {
  yield put({ type: SEARCH_REQUESTED });
}

function createLazily(msec = 1000) {
  let ongoing;
  return function*(task, ...args) {
    if (ongoing && ongoing.isRunning()) {
      ongoing.cancel();
    }
    ongoing = yield fork(function*() {
      yield call(delay, msec);
      yield fork(task, ...args);
    });
  };
}

function* handleRequestSuggest() {
  const lazily = createLazily();
  while (true) {
    const { value } = yield take(CHANGE_QUERY_REQUESTED);
    yield put({ type: CHANGE_QUERY, value });
    const u = yield select(state => state.universityId);
    yield fork(lazily, runRequestSuggest, u, value);
  }
}

function* search() {
  const u = yield select(state => state.universityId);
  const q = (yield select(state => state.query)).trim();
  if (q === "") {
    return yield put({ type: SEARCH_SUCCEEDED, data: { results: null } });
  }
  try {
    const json = yield call(
      apiFetch("GET"),
      "/api/search?" + qs.stringify({ q: q, u: u })
    );
    yield put({ type: SEARCH_SUCCEEDED, data: json });
  } catch (e) {
    yield put({ type: SEARCH_FAILED });
  }
}
// ==== End Incremental Search

function* fetchProfessor(action) {
  try {
    const json = yield call(apiFetch("GET"), "/api/professors/" + action.id);
    yield put({ type: PROFESSOR_SUCCEEDED, data: json });
  } catch (e) {
    yield put({ type: PROFESSOR_FAILED });
  }
}

function* submitComment(action) {
  if (hasBadWord(action.text)) {
    Raven.captureException(new Error("BadMouth: " + action.text));
    yield put({ type: BAD_COMMENT_SUBMITTED });
    return;
  }
  try {
    const json = yield call(
      apiFetch("POST", {
        professorId: action.professorId,
        comment: action.text
      }),
      "/api/comments"
    );
    yield put({ type: NEW_COMMENT_SUCCEEDED, data: json });
  } catch (e) {
    yield put({ type: NEW_COMMENT_FAILED });
  }
}

function* markInappropriate(action) {
  try {
    const json = yield call(
      apiFetch("PUT"),
      "/api/comments/" + action.comment.id
    );
    yield put({
      type: INAPPROPRIATE_SUCCEEDED,
      data: json,
      comment: action.comment
    });
  } catch (e) {
    yield put({ type: INAPPROPRIATE_FAILED });
  }
}

function* voteProfessor(action) {
  try {
    const baseUrl = "/api/professors/" + action.professor.id;
    const url = action.upvote ? baseUrl + "/upvotes" : baseUrl + "/downvotes";
    const json = yield call(apiFetch("POST"), url);
    yield put({
      type: PROF_VOTE_SUCCEEDED,
      data: json,
      professor: action.professor,
      upvote: action.upvote
    });
  } catch (e) {
    yield put({ type: PROF_VOTE_FAILED });
  }
}

function* voteComment(action) {
  try {
    const baseUrl = "/api/comments/" + action.comment.id;
    const url = action.upvote ? baseUrl + "/upvotes" : baseUrl + "/downvotes";
    const json = yield call(apiFetch("POST"), url);
    yield put({
      type: COMMENT_VOTE_SUCCEEDED,
      data: json,
      comment: action.comment,
      upvote: action.upvote
    });
  } catch (e) {
    yield put({ type: COMMENT_VOTE_FAILED });
  }
}

// TODO: Still update query if included in location.search.
// Shoot QUERY_REQUESTED, if in search page.
// If location changes query, send search requests.
function* locationChange(action) {
  const u = yield select(state => state.universityId);
  const match = SEARCH_PAGE_REGEX.exec(action.payload.pathname);
  const universityId = match === null ? u : parseInt(match[1], 10);
  if (universityId !== u) {
    yield put({ type: CHANGE_UNIVERSITY, value: universityId });
  }

  const q = yield select(state => state.query);
  const query =
    qs.parse(window.location.search, { ignoreQueryPrefix: true }).q || "";
  if (match !== null && (query !== "" || query !== q)) {
    yield put({ type: CHANGE_QUERY_REQUESTED, value: query });
  }
}

function* rootSaga() {
  yield fork(handleRequestSuggest);
  yield all([
    takeLatest(USER_FETCH_REQUESTED, fetchUser),
    takeLatest(SEARCH_REQUESTED, search),
    takeLatest(PROFESSOR_REQUESTED, fetchProfessor),
    takeEvery(NEW_COMMENT_REQUESTED, submitComment),
    takeEvery(INAPPROPRIATE_REQUESTED, markInappropriate),
    takeEvery(PROF_VOTE_REQUESTED, voteProfessor),
    takeEvery(COMMENT_VOTE_REQUESTED, voteComment),
    takeLatest(LOCATION_CHANGE, locationChange),
    suggestionsSaga
  ]);
}

export { apiFetch };

export default rootSaga;



// WEBPACK FOOTER //
// ./src/sagas.js